
import { supabase } from '@/integrations/supabase/client';
import { Product } from '@/types';
import { mapDatabaseProductToProduct, prepareProductForInsert, prepareProductForUpdate } from '@/utils/productTransformers';

export const fetchAllProducts = async (): Promise<Product[]> => {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching products:', error);
    throw error;
  }

  return data ? data.map(mapDatabaseProductToProduct) : [];
};

export const addProduct = async (product: Omit<Product, "id" | "status" | "createdAt" | "createdBy">): Promise<Product> => {
  console.log('Adding product:', product);
  
  // Get current user
  const { data: { user } } = await supabase.auth.getUser();
  
  // Prepare product data for insert with status
  const productData = {
    ...prepareProductForInsert(product, user?.id || null),
    status: 'active' // Add the missing status field
  };
  
  console.log('Prepared product data for insert:', productData);

  const { data, error } = await supabase
    .from('products')
    .insert(productData)
    .select()
    .single();

  if (error) {
    console.error('Error adding product:', error);
    throw error;
  }

  console.log('Product added successfully:', data);
  return mapDatabaseProductToProduct(data);
};

export const updateProduct = async (product: Product): Promise<void> => {
  console.log('Updating product:', product);

  const productData = prepareProductForUpdate(product);
  console.log('Prepared product data for update:', productData);

  const { error } = await supabase
    .from('products')
    .update(productData)
    .eq('id', product.id);

  if (error) {
    console.error('Error updating product:', error);
    throw error;
  }

  console.log('Product updated successfully');
};

export const deleteProduct = async (productId: string): Promise<void> => {
  console.log('Deleting product with id:', productId);

  const { error } = await supabase
    .from('products')
    .delete()
    .eq('id', productId);

  if (error) {
    console.error('Error deleting product:', error);
    throw error;
  }

  console.log('Product deleted successfully');
};

export const subscribeToProductChanges = (callback: () => void) => {
  return supabase
    .channel('products-changes')
    .on('postgres_changes', 
      { 
        event: '*', 
        schema: 'public', 
        table: 'products' 
      }, 
      () => {
        console.log('Products table changed, refreshing...');
        callback();
      }
    )
    .subscribe();
};
